<template>
    <v-dialog v-model="dialog" rounded :width="modalSize" persistent>
        <v-card class="d-flex flex-column aq-modal" style="height: 80vh">
            <!-- Header -->
            <v-card-title class="d-flex align-start pa-4 aq-modal-header">
                <v-row class="align-center w-100">
                    <v-col class="d-flex align-center">
                        <span class="text-h6">{{ title }}</span>
                    </v-col>
                    <v-col class="d-flex justify-end">
                        <AQButton
                            variant="outlined"
                            prependIcon="mdi-close"
                            size="small"
                            @click="handleClose"
                        >
                            {{ $t('forms.labels.close') }}
                        </AQButton>
                    </v-col>
                </v-row>
                <v-row class="align-center">
                    <v-col v-if="showLangs" class="d-flex align-center mt-2">
                        <AQSelect
                            v-model="lang"
                            :label="$t('forms.labels.lang')"
                            :items="displayedLangs"
                            outlined
                            hide-details
                            dense
                            rounded
                            style="width: 200px"
                            @input="updateLang"
                        />
                    </v-col>
                </v-row>
            </v-card-title>

            <!-- Divider -->
            <v-divider />

            <!-- Tabs -->
            <v-tabs v-if="getTabs.length" v-model="activeTabIndex" class="aq-modal-tabs">
                <v-tab v-for="(tab, index) in getTabs" :key="index" :disabled="tab.disabled">
                    {{ tab.name }}
                </v-tab>
            </v-tabs>
            <v-divider v-if="getTabs.length" />

            <!-- Content -->
            <v-sheet class="flex-grow-1" style="overflow-y: auto; min-height: 0">
                <v-card-text class="pa-4">
                    <!-- Render all tabs when tabs exist, but only show the active one -->
                    <template v-if="getTabs.length">
                        <div
                            v-for="(tab, index) in getTabs"
                            :key="index"
                            v-show="index === activeTabIndex"
                        >
                            <component
                                :is="tab.component && markRaw(tab.component)"
                                :ref="(el: unknown) => setTabComponentRef(index, el)"
                                v-bind="{
                                    ...$attrs,
                                    ...(props.payload || {}),
                                    ...(tab?.props || {}),
                                    modalTabIndex: index,
                                }"
                                v-on="{ ...$attrs, ...(props.listeners || {}) }"
                                @submit-success="handleChildSubmitSuccess"
                            />
                        </div>
                    </template>
                    <!-- Render single component when no tabs -->
                    <component
                        v-else-if="props.component"
                        :is="props.component && markRaw(props.component)"
                        ref="innerComponent"
                        v-bind="{
                            ...$attrs,
                            ...(props.payload || {}),
                        }"
                        v-on="{ ...$attrs, ...(props.listeners || {}) }"
                        @submit-success="handleChildSubmitSuccess"
                    />
                    <slot v-if="!(props.component || getTabs.length)"></slot>
                    <slot v-if="isConfirmModal" name="body"></slot>
                </v-card-text>
            </v-sheet>

            <!-- Divider -->
            <v-divider />

            <!-- Footer -->
            <v-card-actions
                v-if="showConfirmButton && !noFooter"
                class="pa-4 justify-end aq-modal-footer"
            >
                <v-row class="align-center w-100">
                    <!-- PREVIOUS BUTTON -->
                    <v-col class="d-flex align-center">
                        <v-card-actions>
                            <div v-if="getTabs.length && displayPrevious" class="d-flex">
                                <AQButton
                                    variant="outlined"
                                    prependIcon="mdi-arrow-left"
                                    size="small"
                                    @click="previous"
                                >
                                    {{ $t('pagination.previous') }}
                                </AQButton>
                            </div>
                        </v-card-actions>
                    </v-col>
                    <!-- CONFIRM/NEXT BUTTON -->
                    <v-col v-if="secondButtonLabel" class="d-flex justify-end" cols="auto">
                        <AQButton
                            variant="outlined"
                            :style="{
                                backgroundColor:
                                    secondButtonLabel === t('forms.labels.delete')
                                        ? 'rgb(var(--v-theme-aquassistRed)) !important'
                                        : '',
                            }"
                            color="secondary"
                            @click="handleSecondButton"
                        >
                            {{ secondButtonLabel }}
                        </AQButton>
                    </v-col>

                    <v-col class="d-flex justify-end" cols="auto" v-if="showConfirmButton">
                        <AQButton
                            variant="flat"
                            color="secondary"
                            :class="!displayNext ? 'confirm-modal-btn' : ''"
                            :disabled="isFormInvalid || isConfirming"
                            :loading="isConfirming"
                            @click="displayNext ? next() : debouncedHandleSave()"
                        >
                            {{
                                displayNext
                                    ? t('forms.btn.next')
                                    : isSaveBtnVisible
                                    ? t('forms.btn.save')
                                    : t('forms.btn.confirm')
                            }}
                        </AQButton>
                    </v-col>
                </v-row>
            </v-card-actions>
            <v-card-actions v-else class="noFooter-v-card" />
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
    import { shallowRef, computed, ref, watch, markRaw, provide } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { useStore } from 'vuex';
    import { debounce } from 'lodash';

    import { COMPONENT } from '@/aquassist-shared/constants/types/node';
    import { NodeTree } from '@/interfaces/site-tree.types';
    import { Tab } from '@/store/modules/modalTab';
    import LockService from '@/utilities/lockService';
    import type { ComponentWithGetData } from '@/interfaces/common';
    import { ModalSize } from '@/interfaces/components/AQModal.types';

    const { t } = useI18n();

    // Type guard to check if component has getData method
    function hasGetData(
        component: ComponentWithGetData,
    ): component is ComponentWithGetData & { getData: () => unknown } {
        return typeof (component as { getData?: () => unknown }).getData === 'function';
    }

    const props = withDefaults(
        defineProps<{
            component?: ComponentWithGetData;
            title: string;
            isConfirmDisabled?: boolean;
            isSaveBtnVisible?: boolean;
            dialog?: boolean;
            modelValue?: boolean;
            showNextPrevious?: boolean;
            showLangs?: boolean;
            lang?: string;
            showConfirmButton?: boolean;
            props?: Record<string, unknown>;
            payload?: unknown;
            modalSize?: ModalSize;
            secondButtonLabel?: string;
            skipValidation?: boolean;
            componentRef?: unknown;
            noFooter?: boolean;
            isConfirmModal?: boolean;
            listeners?: Record<string, (...args: unknown[]) => void>;
            modalId?: number;
            closeImmediatelyOnConfirm?: boolean;
            onConfirm?: (data: unknown) => Promise<void> | void;
        }>(),
        {
            isConfirmDisabled: false,
            dialog: true,
            modelValue: false,
            showNextPrevious: true,
            showLangs: false,
            isConfirmModal: false,
            showConfirmButton: true,
            form: false,
            isSaveBtnVisible: false,
            secondButtonLabel: '',
            noFooter: false,
            modalId: 0,
            closeImmediatelyOnConfirm: true,
        },
    );
    const emit = defineEmits<{
        (e: 'confirm', data: unknown): void;
        (e: 'secondButton', data: unknown): void;
        (e: 'close'): void;
        (e: 'update:modelValue', value: boolean): void;
        (e: 'update:lang', value: string): void;
    }>();

    const requestPayload = ref<unknown>();
    const isConfirming = ref(false);

    const innerFormValidity = ref<null | boolean>(null);
    // Store validity per tab index
    const tabValidityMap = ref<Map<number, boolean>>(new Map());

    // Define the method you want to expose
    function onFormValidityChange(isValid: boolean, tabIndex?: number) {
        innerFormValidity.value = isValid;
        // Store validity for specific tab, active tab will be 0 if no tabIndex is provided
        // as all components load at the same time
        if (getTabs.value.length > 0) {
            const targetIndex = tabIndex !== undefined ? tabIndex : activeTabIndex.value;
            tabValidityMap.value.set(targetIndex, isValid);
        }
    }
    function setRequestPayload(data: unknown) {
        requestPayload.value = data;
    }

    // Provide the method to descendants
    provide('modalMethods', {
        onFormValidityChange,
        setRequestPayload,
    });

    const dialog = shallowRef(props.dialog || props.modelValue || false);
    // to fix when lang store is not available
    const displayedLangs = computed(() => {
        return props.showLangs ? ['en', 'fr'] : [];
    });

    // Store
    const store = useStore();
    const { dispatch } = store;

    // Getters
    const getTabs = computed<Tab[]>(() => store.getters['modalTab/getTabs'](props.modalId));
    const activeTabIndex = computed<number>({
        get: () => store.getters['modalTab/activeTab'](props.modalId),
        set: (val: number) => {
            dispatch('modalTab/changeActiveTab', { tabIndex: val, modalId: props.modalId });
        },
    });

    // computed
    const displayPrevious = computed(() => activeTabIndex.value > 0);
    const displayNext = computed(() => getTabs.value.length - 1 > activeTabIndex.value);
    const modalSize = computed(() => {
        switch (props.modalSize) {
            case 'small':
                return 500;
            case 'large':
                return 2200;
            case 'fullScreen':
                return 'fullScreen';
            default:
                return 1100; //medium
        }
    });

    // Vérifie si le formulaire interne est invalide
    const isFormInvalid = computed(() => {
        if (props.skipValidation) return false;
        if (props.isConfirmDisabled) return true;
        // If there are tabs, check validity
        if (getTabs.value.length > 0) {
            // For Next button: only check current tab
            if (displayNext.value) {
                const currentTabValidity = tabValidityMap.value.get(activeTabIndex.value);
                if (currentTabValidity !== undefined) {
                    return !currentTabValidity;
                }
                if (innerFormValidity.value !== null) {
                    return !innerFormValidity.value;
                }
                return true; // Disable until we know validity
            }

            // For Confirm button (last tab): check ALL visible tabs that have reported validity
            // This ensures all previous visible forms are valid before allowing confirmation
            for (const [tabIndex, isValid] of tabValidityMap.value.entries()) {
                const tab = getTabs.value[tabIndex];
                if (tab?.disabled) continue;
                if (!isValid) {
                    return true; // Disable if any visible tab is invalid
                }
            }

            // Also check current tab validity
            const currentTabValidity = tabValidityMap.value.get(activeTabIndex.value);
            if (currentTabValidity !== undefined) {
                return !currentTabValidity;
            }
            if (innerFormValidity.value !== null) {
                return !innerFormValidity.value;
            }
            return true; // Disable until we know validity
        }

        // Non-tab modal: respect child validity if provided
        if (innerFormValidity.value !== null) return !innerFormValidity.value;

        return props.isConfirmDisabled;
    });

    // Watch
    watch(
        () => props.dialog,
        (newVal) => {
            if (newVal !== undefined) {
                dialog.value = newVal;
            }
        },
        { immediate: true },
    );

    watch(
        () => props.modelValue,
        (newVal) => {
            if (newVal !== undefined) {
                dialog.value = newVal;
            }
        },
        { immediate: true },
    );

    watch(dialog, (newVal) => {
        emit('update:modelValue', newVal);
        if (!newVal) {
            isConfirming.value = false;
        }
    });
    const innerComponent = ref<ComponentWithGetData | null>(null);
    const tabComponentRefs = ref<Array<{ getData?: () => unknown } | null>>([]);

    const setTabComponentRef = (index: number, el: unknown) => {
        if (el === null) {
            // Component is being unmounted, safely set to null
            tabComponentRefs.value[index] = null;
        } else {
            tabComponentRefs.value[index] = el as { getData?: () => unknown } | null;
        }
    };

    defineExpose({
        getInnerComponent: () => innerComponent.value,
    });

    const releasLock = () => {
        const { actionName, node, lockAcquired } = (props.payload || {}) as unknown as {
            actionName?: string;
            lockAcquired?: boolean;
            node?: NodeTree;
        };
        if (lockAcquired && actionName && node && node.nodeType === COMPONENT) {
            LockService.releaseLock(node.ref || node.id, actionName);
        }
    };

    // handlers
    function handleClose() {
        emit('close');
        dialog.value = false;
        // Clear validity map when closing modal
        tabValidityMap.value.clear();
        innerFormValidity.value = null;
        releasLock();
    }

    function handleChildSubmitSuccess(data: unknown) {
        emit('confirm', data);
        dialog.value = false;
        // Clear validity map when closing modal
        tabValidityMap.value.clear();
        innerFormValidity.value = null;
    }

    async function handleSave() {
        if (isConfirming.value) return;
        isConfirming.value = true;
        try {
            let data: unknown = null;
            // If tabs exist, collect data from all tab components - just for multi step forms
            if (getTabs.value.length > 0) {
                const allData: Record<string, unknown> = {};
                tabComponentRefs.value.forEach((tabRef: { getData?: () => unknown } | null) => {
                    if (tabRef && typeof tabRef.getData === 'function') {
                        const tabData = tabRef.getData();
                        Object.assign(allData, tabData);
                    }
                });
                data = allData;
            } else {
                //  single tab form
                const component = innerComponent.value;
                if (component && hasGetData(component)) {
                    // await so forms whose getData is async (e.g. graph export) resolve;
                    // a synchronous getData passes through unchanged.
                    data = await component.getData();
                } else if (requestPayload.value) {
                    data = requestPayload.value;
                }
            }
            if (props.onConfirm) {
                await props.onConfirm(data);
            } else {
                emit('confirm', data);
            }
            if (props.closeImmediatelyOnConfirm) {
                dialog.value = false;
            }
        } catch (error) {
            console.error('Error in handleSave:', error);
            isConfirming.value = false;
            if (props.closeImmediatelyOnConfirm) {
                dialog.value = false;
            }
        } finally {
            releasLock();
        }
        // Clear validity map when closing modal
        tabValidityMap.value.clear();
        innerFormValidity.value = null;
    }

    const debouncedHandleSave = debounce(handleSave, 500, {
        leading: true,
        trailing: false,
    });

    function handleSecondButton() {
        // If tabs exist, collect data from all tab components - just for multi step forms
        if (getTabs.value.length > 0) {
            const allData: Record<string, unknown> = {};
            tabComponentRefs.value.forEach((tabRef: { getData?: () => unknown } | null) => {
                if (tabRef && typeof tabRef.getData === 'function') {
                    const data = tabRef.getData();
                    Object.assign(allData, data);
                }
            });
            emit('secondButton', allData);
        } else {
            const component = innerComponent.value;

            if (component && hasGetData(component)) {
                const data = component.getData();
                emit('secondButton', data);
            } else {
                emit('secondButton', null);
            }
        }
        dialog.value = false;

        dispatch('modalTab/clearTabs', props.modalId);
    }

    function updateLang(lang: string) {
        emit('update:lang', lang);
    }

    async function previous() {
        // Reset validity when changing tabs so new tab can report its state
        innerFormValidity.value = null;
        const prevIndex = store.getters['modalTab/previousVisibleTab'](
            props.modalId,
            activeTabIndex.value,
        );
        await dispatch('modalTab/changeActiveTab', {
            tabIndex: prevIndex,
            modalId: props.modalId,
        });
    }

    async function next() {
        // Reset validity when changing tabs so new tab can report its state
        innerFormValidity.value = null;
        const nextIndex = store.getters['modalTab/nextVisibleTab'](
            props.modalId,
            activeTabIndex.value,
        );
        await dispatch('modalTab/changeActiveTab', {
            tabIndex: nextIndex,
            modalId: props.modalId,
        });
    }

    const lang = ref(props.lang || 'en');
</script>
